import { env } from '../env';

export const INDEXDB_TABLE_NAMES = {
  service_cache: 'service_cache',
  icons_mcute: 'icons_mcute',
} as const;

export type TIndexDBTableNames = (typeof INDEXDB_TABLE_NAMES)[keyof typeof INDEXDB_TABLE_NAMES];

interface IAstroPayIndexDbTableSetting {
  tableName: TIndexDBTableNames;
  keyPath: string;
  dropOnVersionChange: boolean;
  autoIncrement?: boolean;
}
interface IAstroPayIndexDbSetting {
  dbName: string;
  tables: IAstroPayIndexDbTableSetting[];
}

export class APUtilIndexedDB {
  private static settings: {
    dbName: string;
    tables: IAstroPayIndexDbTableSetting[];
  };
  private static dbInstance: IDBDatabase | null = null;

  static init(data: IAstroPayIndexDbSetting) {
    this.settings = { ...data };
    this.openDatabase(data.tables);
  }

  private static async openDatabase(tables?: IAstroPayIndexDbTableSetting[]): Promise<IDBDatabase> {
    if (this.dbInstance) return Promise.resolve(this.dbInstance);

    return new Promise((resolve, reject) => {
      const _version = APUtilIndexedDB.parseAppVersion();
      const request = indexedDB.open(this.settings.dbName, _version);

      if (tables) {
        request.onupgradeneeded = (event) => {
          const db = request.result;
          const oldVersion = event.oldVersion;

          tables.forEach((table) => {
            if (table.dropOnVersionChange && oldVersion < _version) {
              if (db.objectStoreNames.contains(table.tableName)) {
                db.deleteObjectStore(table.tableName);
              }
            }
            if (!db.objectStoreNames.contains(table.tableName)) {
              db.createObjectStore(table.tableName, {
                keyPath: table.keyPath,
                autoIncrement: table.autoIncrement,
              });
            }
          });
        };
      }

      request.onsuccess = () => {
        this.dbInstance = request.result;
        resolve(this.dbInstance);
      };

      request.onerror = () => {
        console.error('Error openDatabase', request.error);
        const deleteRequest = indexedDB.deleteDatabase(this.settings.dbName);
        deleteRequest.onsuccess = () => {
          const retryRequest = indexedDB.open(this.settings.dbName, _version);

          if (tables) {
            retryRequest.onupgradeneeded = () => {
              const db = retryRequest.result;
              tables.forEach((table) => {
                if (!db.objectStoreNames.contains(table.tableName)) {
                  db.createObjectStore(table.tableName, {
                    keyPath: table.keyPath,
                    autoIncrement: table.autoIncrement,
                  });
                }
              });
            };
          }

          retryRequest.onsuccess = () => {
            this.dbInstance = retryRequest.result;
            resolve(this.dbInstance);
          };

          retryRequest.onerror = () => {
            console.error('Error retrying openDatabase', retryRequest.error);
            reject(retryRequest.error);
          };
        };

        deleteRequest.onerror = () => {
          console.error('Error deleting database', deleteRequest.error);
          reject(deleteRequest.error);
        };
      };
    });
  }

  private static parseAppVersion() {
    let cleanedVersion = env.APP_VERSION.replace(/-\D+/g, '0');
    cleanedVersion = cleanedVersion.replace(/[^0-9]/g, '');
    const _version = parseInt(cleanedVersion, 10);
    return _version === 0 ? 1 : _version;
  }

  static async clearDatabase() {
    try {
      await indexedDB.deleteDatabase(this.settings.dbName);
      this.dbInstance = null;
      console.info('Database cleared');
    } catch (e) {
      console.error('Error clearDatabase', e);
    }
  }

  static async clearRows(tableName: TIndexDBTableNames) {
    try {
      const db = await this.openDatabase();
      const transaction = db.transaction(tableName, 'readwrite');
      const store = transaction.objectStore(tableName);
      store.clear();
    } catch (e) {
      this.clearDatabase();
      console.error('Error clearRows', e);
    }
  }

  static async deleteRow(tableName: TIndexDBTableNames, key: IDBValidKey) {
    try {
      const db = await this.openDatabase();
      const transaction = db.transaction(tableName, 'readwrite');
      const store = transaction.objectStore(tableName);
      store.delete(key);
    } catch (e) {
      this.clearDatabase();
      console.error('Error deleteRow', e);
    }
  }

  static async addTable(
    tableName: TIndexDBTableNames,
    keyPath = 'id',
    autoIncrement = true
  ): Promise<void> {
    try {
      const db = await this.openDatabase();
      const version = db.version + 1;
      db.close();
      this.dbInstance = null;
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(this.settings.dbName, version);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(tableName)) {
            db.createObjectStore(tableName, { keyPath, autoIncrement });
          }
        };
        request.onsuccess = () => {
          this.dbInstance = request.result;
          resolve();
        };
        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (e) {
      this.clearDatabase();
      return Promise.reject(e);
    }
  }

  static async removeTable(tableName: string): Promise<void> {
    try {
      const db = await this.openDatabase();
      const version = db.version + 1;
      db.close();
      this.dbInstance = null;
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(this.settings.dbName, version);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (db.objectStoreNames.contains(tableName)) {
            db.deleteObjectStore(tableName);
          }
        };
        request.onsuccess = () => {
          this.dbInstance = request.result;
          resolve();
        };
        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (e) {
      console.error('Error removeTable', e);
      return Promise.reject(e);
    }
  }

  static async addRow(
    tableName: TIndexDBTableNames,
    idKey: string,
    data: Record<string, unknown>
  ): Promise<'updated' | 'added'> {
    try {
      const instance = await this.openDatabase();
      if (!instance.objectStoreNames.contains(tableName)) {
        return Promise.reject(`Table ${tableName} does not exist in the database`);
      }
      const transaction = instance.transaction(tableName, 'readwrite');
      const store = transaction.objectStore(tableName);

      return new Promise((resolve, reject) => {
        const getRequest = store.get(data[idKey] as IDBValidKey);
        getRequest.onsuccess = () => {
          const existingData = getRequest.result;
          const request = store.put(data);

          request.onsuccess = () => {
            resolve(existingData ? 'updated' : 'added');
          };

          request.onerror = (event) => {
            reject(`Error adding/updating data: ${event}`);
          };
        };

        getRequest.onerror = (event) => {
          reject(`Error getting data: ${event}`);
        };
      });
    } catch (e) {
      console.error('Error addRow', e);
      return Promise.reject(e);
    }
  }

  static async getAllRows(tableName: TIndexDBTableNames) {
    try {
      const instance = await this.openDatabase();
      if (!instance.objectStoreNames.contains(tableName)) {
        return Promise.reject(`Table ${tableName} does not exist in the database`);
      }
      const transaction = instance.transaction(tableName, 'readonly');
      const store = transaction.objectStore(tableName);
      return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => {
          resolve(request.result);
        };
        request.onerror = (error) => {
          console.error('ERROR getAllRows', error);
          console.info('Error info', tableName);

          reject(request.error);
        };
      });
    } catch (e) {
      this.clearDatabase();
      console.error('Error getAllRows', e);
      return Promise.reject(e);
    }
  }

  static async getRowBy(tableName: TIndexDBTableNames, key: IDBValidKey) {
    try {
      const instance = await this.openDatabase();

      if (!instance.objectStoreNames.contains(tableName)) {
        return Promise.reject(`Table ${tableName} does not exist in the database`);
      }

      const transaction = instance.transaction(tableName, 'readonly');
      const store = transaction.objectStore(tableName);
      return new Promise((resolve, reject) => {
        const request = store.get(key);
        request.onsuccess = () => {
          resolve(request.result);
        };
        request.onerror = (error) => {
          console.error('ERROR getRowBy', error);
          console.info('Error info', tableName, key);
          reject(request.error);
        };
      });
    } catch (e) {
      this.clearDatabase();
      console.error('Error getRowBy', e);
      return Promise.reject(e);
    }
  }
}
